import React, { useState, useEffect } from 'react';

interface TabsProps {
  tabs: string[];
  defaultValue?: string;
  value?: string;
  onChange?: (selectedTab: string) => void;
  className?: string;
}

const Tabs = ({
  tabs,
  defaultValue,
  value,
  onChange,
  className = ''
}: TabsProps) => {
  const [internalValue, setInternalValue] = useState(defaultValue || tabs[0]);
  const activeValue = value || internalValue;

  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  const handleChange = (newValue: string) => {
    if (!value) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const getButtonClasses = (tab: string, index: number) => {
    const isActive = tab === activeValue;
    const isFirst = index === 0;
    const isLast = index === tabs.length - 1;

    return [
      'inline-block w-full p-4 focus:ring-2 focus:ring-tertiary-light focus:outline-none',
      isActive
        ? 'text-gray-900 bg-tertiary-light dark:bg-gray-700 dark:text-white'
        : 'bg-white hover:text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:text-white',
      isFirst ? 'rounded-s-lg border-r' : '',
      isLast ? 'rounded-e-lg border-l' : 'border-r',
      'border-gray-200 dark:border-gray-700'
    ].join(' ');
  };

  return (
    <div className={className}>
      {/* Mobile Dropdown */}
      <div className="sm:hidden">
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
        <select
          id="tabs"
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          value={activeValue}
          onChange={(e) => handleChange(e.target.value)}
        >
          {tabs.map((tab) => (
            <option key={tab} value={tab}>
              {tab}
            </option>
          ))}
        </select>
      </div>

      {/* Desktop Tabs */}
      <ul className="hidden text-sm font-medium text-center text-gray-500 rounded-lg shadow-sm sm:flex dark:divide-gray-700 dark:text-gray-400">
        {tabs.map((tab, index) => (
          <li key={tab} className="w-full focus-within:z-10">
            <button
              type="button"
              onClick={() => handleChange(tab)}
              className={getButtonClasses(tab, index)}
              aria-current={tab === activeValue ? 'page' : undefined}
            >
              {tab}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Tabs;